import { useMemo } from 'react';
import {
    Button,
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Notice,
} from '@origo/origo-ui';
import { PencilSimpleIcon, PlusIcon, UsersIcon } from '@phosphor-icons/react';
import type { Område } from '@origo/seksjon';
import type { DelområdegruppeView } from './NavPanel';
import styles from './DelomradeAdmin.module.css';

interface Props {
    /** Delområdetype-tre fra useAdminAreas (groupKey → children). */
    areas: Område[];
    /** Eksisterende grupper, allerede ferdig prosessert i AdminPage. */
    grupper: readonly DelområdegruppeView[];
    /** Åpne gruppe-editoren (panel m/ kart) for å opprette en ny gruppe. */
    onNewGroup: () => void;
    /** Åpne gruppe-editoren (panel m/ kart) for å redigere en valgt gruppe. */
    onEditGroup: (g: DelområdegruppeView) => void;
}

export function DelomradeAdmin({ areas, grupper, onNewGroup, onEditGroup }: Readonly<Props>) {
    const grupperByType = useMemo(() => {
        const map = new Map<string, DelområdegruppeView[]>();
        for (const g of grupper) {
            if (!map.has(g.delomradetype)) map.set(g.delomradetype, []);
            map.get(g.delomradetype)!.push(g);
        }
        return map;
    }, [grupper]);

    const hasGroups = grupper.length > 0;

    return (
        <div className={styles.root}>
            {hasGroups ? (
                <>
                    <header className={styles.header}>
                        <div>
                            <h2 className={styles.title}>Delområdegrupper</h2>
                            <p className={styles.subtitle}>
                                Grupperer flere delområder under samme type så du kan skrive felles veiledning én gang.
                            </p>
                        </div>
                        <Button variant="primary" leftIcon={<PlusIcon size={16} />} onClick={onNewGroup}>
                            Ny gruppe
                        </Button>
                    </header>

                    <div className={styles.list}>
                        {areas.map((type) => {
                            const typeGroups = grupperByType.get(type.id) ?? [];
                            if (typeGroups.length === 0) return null;
                            return (
                                <section key={type.id} className={styles.typeSection}>
                                    <h3 className={styles.typeHeading}>{type.navn}</h3>
                                    {typeGroups.map((g) => (
                                        <Card key={g.id}>
                                            <CardHeader>
                                                <div className={styles.cardHeaderRow}>
                                                    <span className={styles.cardTitle}>{g.navn}</span>
                                                    <IconButton
                                                        icon={<PencilSimpleIcon size={16} />}
                                                        label="Rediger gruppe"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onEditGroup(g)}
                                                    />
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <p className={styles.memberCount}>
                                                    <UsersIcon size={14} />
                                                    <span>
                                                        {g.memberDelomradeIds.length}{' '}
                                                        {g.memberDelomradeIds.length === 1 ? 'medlem' : 'medlemmer'}
                                                    </span>
                                                </p>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </section>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className={styles.empty}>
                    <h2 className={styles.emptyTitle}>Delområdegrupper</h2>
                    <p className={styles.emptyBody}>
                        Lag en gruppe når flere delområder under samme type trenger samme avvikende
                        veiledning. Eksempel: 4 av 10 boligfelt har høydebegrensning — gruppér dem,
                        og skriv teksten én gang.
                    </p>
                    <Button variant="primary" leftIcon={<PlusIcon size={16} />} onClick={onNewGroup}>
                        Ny gruppe
                    </Button>
                </div>
            )}

            {areas.length === 0 && (
                <Notice variant="info">
                    Planen har ingen delområder, så du kan ikke lage grupper enda.
                </Notice>
            )}
        </div>
    );
}
